import { capitalize } from 'affiny-core/helpers/string/capitalize';
import { IMemberChoices } from 'affiny-core/modules/api/models/memberchoice/memberchoice.types';
import { IAnswer } from 'affiny-core/modules/api/models/reflist/reflist.types';
import { getAnswersByTextSearch } from 'affiny-core/ui/Question/searchQuestion/helpers';
import { ChipsList } from 'affiny-ui/src';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Flex } from '../primitives';
import { TextInputAutocomplete } from '../text-input-autocomplete';
import rem from '../../utils/rem';

interface IProps {
  title: string;
  label: string;
  placeholder: string;
  answers?: IAnswer[];
  defaultAnswers?: IAnswer[];
  selectedAnswers?: Partial<IMemberChoices>;
  onChangeAnswer: (label: string, answer: number[]) => void;
}

const SearchCriterion = ({
  title,
  label,
  placeholder,
  answers = [],
  defaultAnswers = [],
  selectedAnswers,
  onChangeAnswer,
}: IProps) => {
  const [searchResults, setResults] = useState<
    Array<{ id: string; label: string }>
  >([]);

  const handleAnswerChanged = useCallback(
    (id: number) => (e: React.SyntheticEvent) => {
      onChangeAnswer(label, [id]);
    },
    [label, onChangeAnswer]
  );

  const handleSearchValueSelected = useCallback(
    (id?: string) => {
      if (id) onChangeAnswer(label, [Number(id)]);
      else onChangeAnswer(label, [defaultAnswers[0].id]);
    },
    [onChangeAnswer, label, defaultAnswers]
  );

  const handleSearch = useCallback(
    (value: string) => {
      if (answers)
        setResults(
          getAnswersByTextSearch(
            answers,
            value,
            defaultAnswers.map(item => item.id)
          ).map(item => ({
            id: String(item.id),
            label: item.label,
          }))
        );
    },
    [answers, defaultAnswers]
  );

  const chips = defaultAnswers?.map(({ id, label: answerLabel }) => ({
    isSelected: selectedAnswers?.[label]?.[0] === id ?? false,
    onClick: handleAnswerChanged(id),
    label: capitalize(answerLabel) as string,
  }));

  return (
    <Flex flexDirection="column" mb={rem(46)}>
      <Flex flexWrap="wrap">
        <SChipsList
          chips={chips}
          isMultiChoice
          readOnly={false}
          title={title}
        />
        <TextInputAutocomplete
          placeholder={placeholder}
          search={handleSearch}
          searchResults={searchResults}
          selectedValue={
            selectedAnswers &&
            !defaultAnswers
              .map(item => item.id)
              .includes(selectedAnswers[label][0])
              ? (answers.find(
                  item => item.id === selectedAnswers[label][0]
                ) as IAnswer).label
              : ''
          }
          onValueSelected={handleSearchValueSelected}
        />
      </Flex>
    </Flex>
  );
};

const SChipsList = styled(ChipsList)`
  margin-bottom: ${({ theme }) => theme.space.medium};
`;

export default SearchCriterion;
